import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
  LineChart,
  Line
} from 'recharts';
import {
  LocalParking,
  ElectricScooter,
  EmojiEvents,
  Business,
  FileDownload,
  Description,
  AttachMoney,
  Percent,
  Receipt,
  TrendingUp
} from '@mui/icons-material';
import moment from 'moment';
import axios from '../../../resources/axios';
import { Parking } from '../../../resources/types/parkingsTypes';
import { ManagementCompany, ParkingContractStats } from '../../../resources/types/managementCompaniesTypes';

interface Props {
  parkings: Parking[];
  companies: ManagementCompany[];
}

const PALETTE = ['#4ECDC4', '#3BA99F', '#7EDDD6', '#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#9B5DE5', '#F15BB5', '#00BBF9'];

const StatCard = ({
  icon, label, value, accent
}: { icon: React.ReactNode; label: string; value: string | number; accent: string }) => {
  const theme = useTheme();
  return (
    <Card sx={{
      borderRadius: 2,
      border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none',
      background: `linear-gradient(135deg, ${accent}15 0%, ${accent}05 100%)`,
      height: '100%'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            bgcolor: `${accent}25`, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: accent, lineHeight: 1.2 }}>
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const ParkingsStatsSection = ({ parkings, companies }: Props) => {
  const theme = useTheme();

  const [reportCompany, setReportCompany] = useState<string>('');
  const [reportFrom, setReportFrom] = useState<string>(moment().startOf('month').format('YYYY-MM-DD'));
  const [reportTo, setReportTo] = useState<string>(moment().endOf('month').format('YYYY-MM-DD'));
  const [downloading, setDownloading] = useState<'json' | 'excel' | null>(null);
  const [reportData, setReportData] = useState<ParkingContractStats[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const totalParkings = parkings.length;
  const totalScooters = useMemo(() => parkings.reduce((s, p) => s + (p.scooters?.length || 0), 0), [parkings]);
  const activeParkings = useMemo(() => parkings.filter(p => (p.scooters?.length || 0) > 0).length, [parkings]);

  const topParkings = useMemo(() => {
    return [...parkings]
      .map(p => ({ name: p.name, scooters: p.scooters?.length || 0 }))
      .sort((a, b) => b.scooters - a.scooters)
      .slice(0, 10);
  }, [parkings]);

  const topHotel = topParkings[0];

  const byCompany = useMemo(() => {
    const map = new Map<string, { name: string; scooters: number; parkings: number }>();
    const noCompanyKey = '__none__';
    parkings.forEach(p => {
      const cid = p.managementCompany || noCompanyKey;
      const cname = cid === noCompanyKey
        ? 'Без компанії'
        : (companies.find(c => c._id === cid)?.name || 'Невідома');
      const existing = map.get(cid) || { name: cname, scooters: 0, parkings: 0 };
      existing.scooters += p.scooters?.length || 0;
      existing.parkings += 1;
      map.set(cid, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.scooters - a.scooters);
  }, [parkings, companies]);

  const fetchReport = useCallback(async () => {
    if (!reportFrom || !reportTo) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const params: any = {
        dateFrom: reportFrom,
        dateTo: reportTo,
        format: 'json'
      };
      if (reportCompany) params.companyId = reportCompany;
      const response = await axios.get('/management_companies/parking_contracts_stats', { params });
      setReportData(response.data?.data || []);
    } catch (e: any) {
      console.error('Report fetch failed', e);
      setReportError('Не вдалося завантажити дані звіту');
      setReportData([]);
    } finally {
      setReportLoading(false);
    }
  }, [reportFrom, reportTo, reportCompany]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const reportTotals = useMemo(() => {
    return reportData.reduce(
      (acc, r) => ({
        contracts: acc.contracts + 1,
        contractAmount: acc.contractAmount + (r.contractAmount || 0),
        commissionAmount: acc.commissionAmount + (r.commissionAmount || 0)
      }),
      { contracts: 0, contractAmount: 0, commissionAmount: 0 }
    );
  }, [reportData]);

  const reportByDay = useMemo(() => {
    const map = new Map<string, { date: string; contracts: number; contractAmount: number; commissionAmount: number }>();
    reportData.forEach(r => {
      const d = moment(r.createdAt).format('YYYY-MM-DD');
      const existing = map.get(d) || { date: d, contracts: 0, contractAmount: 0, commissionAmount: 0 };
      existing.contracts += 1;
      existing.contractAmount += r.contractAmount || 0;
      existing.commissionAmount += r.commissionAmount || 0;
      map.set(d, existing);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [reportData]);

  const reportByParking = useMemo(() => {
    const map = new Map<string, { name: string; contracts: number; contractAmount: number; commissionAmount: number }>();
    reportData.forEach(r => {
      const key = r.parkingName || '—';
      const existing = map.get(key) || { name: key, contracts: 0, contractAmount: 0, commissionAmount: 0 };
      existing.contracts += 1;
      existing.contractAmount += r.contractAmount || 0;
      existing.commissionAmount += r.commissionAmount || 0;
      map.set(key, existing);
    });
    return Array.from(map.values())
      .sort((a, b) => b.contractAmount - a.contractAmount)
      .slice(0, 10);
  }, [reportData]);

  const fmtMoney = (v: number) => `€${v.toFixed(2)}`;

  const handleDownload = async (format: 'json' | 'excel') => {
    setDownloading(format);
    try {
      const params: any = {
        dateFrom: reportFrom,
        dateTo: reportTo,
        format
      };
      if (reportCompany) params.companyId = reportCompany;

      const response = await axios.get('/management_companies/parking_contracts_stats', {
        params,
        responseType: format === 'excel' ? 'blob' : 'json'
      });

      const companyLabel = reportCompany
        ? (companies.find(c => c._id === reportCompany)?.name || 'company')
        : 'all_companies';
      const safeLabel = companyLabel.replace(/[^\w]+/g, '_');
      const filename = `parking_contracts_${safeLabel}_${reportFrom}_${reportTo}.${format === 'excel' ? 'xlsx' : 'json'}`;

      let blob: Blob;
      if (format === 'excel') {
        blob = response.data;
      } else {
        blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error('Report download failed', e);
      window.alert('Не вдалося завантажити звіт. Перевірте параметри.');
    } finally {
      setDownloading(null);
    }
  };

  const tooltipStyle = {
    contentStyle: {
      backgroundColor: theme.palette.mode === 'light' ? '#FFF' : '#2A2A2A',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary
    },
    labelStyle: { color: theme.palette.text.primary },
    itemStyle: { color: theme.palette.text.primary }
  } as any;

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<LocalParking />} label="Парковок усього" value={totalParkings} accent="#4ECDC4" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<ElectricScooter />} label="Самокатів на парковках" value={totalScooters} accent="#3BA99F" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<LocalParking />} label="Активних (зі скутерами)" value={`${activeParkings} / ${totalParkings}`} accent="#4D96FF" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<EmojiEvents />}
            label="Топ-готель"
            value={topHotel ? `${topHotel.name.slice(0, 14)}${topHotel.name.length > 14 ? '…' : ''}` : '—'}
            accent="#FFB400"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{
            p: 2, borderRadius: 2,
            border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ElectricScooter sx={{ color: '#4ECDC4' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Топ-10 готелів за кількістю самокатів
              </Typography>
            </Box>
            {topParkings.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                <Typography>Немає даних для відображення</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topParkings} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                    angle={-25}
                    textAnchor="end"
                    interval={0}
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} allowDecimals={false} />
                  <RTooltip {...tooltipStyle} />
                  <Bar dataKey="scooters" fill="#4ECDC4" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{
            p: 2, borderRadius: 2,
            border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Business sx={{ color: '#4ECDC4' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Розподіл по компаніях
              </Typography>
            </Box>
            {byCompany.length === 0 || totalScooters === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                <Typography>Немає даних</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byCompany}
                    dataKey="scooters"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    innerRadius={50}
                    paddingAngle={2}
                  >
                    {byCompany.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <RTooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12, color: theme.palette.text.primary }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{
            p: 2, borderRadius: 2,
            border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : 'none'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Description sx={{ color: '#4ECDC4' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Звіт по поїздках для готелів
              </Typography>
              {reportLoading && <CircularProgress size={18} sx={{ color: '#4ECDC4', ml: 1 }} />}
            </Box>
            <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
              За замовчуванням показуються дані за поточний місяць. Виберіть компанію та змініть діапазон, якщо потрібно.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Компанія"
                  value={reportCompany}
                  onChange={(e) => setReportCompany(e.target.value)}
                >
                  <MenuItem value="">Усі компанії</MenuItem>
                  {companies.map(c => (
                    <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="З"
                  value={reportFrom}
                  onChange={(e) => setReportFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="По"
                  value={reportTo}
                  onChange={(e) => setReportTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box sx={{ display: 'flex', gap: 1, height: '100%' }}>
                  <Tooltip title="Завантажити Excel">
                    <span style={{ flex: 1 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={downloading !== null || !reportFrom || !reportTo}
                        onClick={() => handleDownload('excel')}
                        startIcon={downloading === 'excel' ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
                        sx={{
                          bgcolor: '#4ECDC4',
                          '&:hover': { bgcolor: '#3BA99F' }
                        }}
                      >
                        Excel
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Завантажити JSON">
                    <span>
                      <Button
                        variant="outlined"
                        disabled={downloading !== null || !reportFrom || !reportTo}
                        onClick={() => handleDownload('json')}
                        sx={{
                          borderColor: '#4ECDC4',
                          color: '#4ECDC4',
                          minWidth: 0,
                          '&:hover': { borderColor: '#3BA99F', bgcolor: 'rgba(78,205,196,0.08)' }
                        }}
                      >
                        {downloading === 'json' ? <CircularProgress size={16} sx={{ color: '#4ECDC4' }} /> : 'JSON'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>

            {reportError && (
              <Box sx={{
                mt: 2, p: 1.5, borderRadius: 1,
                bgcolor: 'rgba(244, 67, 54, 0.08)',
                color: '#F44336', fontSize: 14
              }}>
                {reportError}
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <StatCard icon={<Receipt />} label="Поїздок" value={reportTotals.contracts} accent="#4ECDC4" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard icon={<AttachMoney />} label="Сума контрактів" value={fmtMoney(reportTotals.contractAmount)} accent="#3BA99F" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard icon={<Percent />} label="Сума комісії" value={fmtMoney(reportTotals.commissionAmount)} accent="#FFB400" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard
                    icon={<TrendingUp />}
                    label="Середній чек"
                    value={reportTotals.contracts > 0 ? fmtMoney(reportTotals.contractAmount / reportTotals.contracts) : '—'}
                    accent="#4D96FF"
                  />
                </Grid>
              </Grid>

              {reportData.length === 0 && !reportLoading ? (
                <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                  <Typography>За обраний період даних немає</Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Box sx={{
                      p: 2, borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                        Динаміка за днями
                      </Typography>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={reportByDay} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                            tickFormatter={(d) => moment(d).format('DD.MM')}
                          />
                          <YAxis yAxisId="left" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                          <RTooltip {...tooltipStyle} labelFormatter={(d) => moment(d as string).format('DD.MM.YYYY')} />
                          <Legend wrapperStyle={{ fontSize: 12, color: theme.palette.text.primary }} />
                          <Line yAxisId="left" type="monotone" dataKey="contractAmount" stroke="#4ECDC4" strokeWidth={2} name="Сума €" dot={{ r: 3 }} />
                          <Line yAxisId="left" type="monotone" dataKey="commissionAmount" stroke="#FFB400" strokeWidth={2} name="Комісія €" dot={{ r: 3 }} />
                          <Line yAxisId="right" type="monotone" dataKey="contracts" stroke="#4D96FF" strokeWidth={2} name="Поїздок" dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Box sx={{
                      p: 2, borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                        Топ готелів за виручкою
                      </Typography>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={reportByParking} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                          <YAxis
                            type="category"
                            dataKey="name"
                            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                            width={110}
                          />
                          <RTooltip {...tooltipStyle} formatter={(v: any, n: any) => n === 'Поїздок' ? v : `€${Number(v).toFixed(2)}`} />
                          <Legend wrapperStyle={{ fontSize: 12, color: theme.palette.text.primary }} />
                          <Bar dataKey="contractAmount" fill="#4ECDC4" name="Сума €" radius={[0, 6, 6, 0]} />
                          <Bar dataKey="commissionAmount" fill="#FFB400" name="Комісія €" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ParkingsStatsSection;
